export type SequencerStep = {
  key?: string;
  init?: () => void;
  handler: (data: any, resolve: () => void) => void | Promise<void>;
  timeout?: number;
};

/**
 * Websocket guarantees:
 * - in-order delivery
 *
 * Sequencer guarantees:
 * - if a message is not consumed by a handler, it will be read by the next handler (no messages will disappear between handlers)
 *
 * Sequencer gotchas:
 * - a handler may read multiple incoming messages: handlers are responsible for ignoring and not consuming irrelevant messages
 */

export class Sequencer {
  private ws: WebSocket;
  private deferred: Promise<void>;
  private _resolve?: (value: void | PromiseLike<void>) => void;
  private _reject?: (reason?: any) => void;
  private timeout?: number;
  private messageBuffer: any[] = [];
  private handlers: SequencerStep[];
  private boundHandler: Sequencer["handler"] = this.handler.bind(this);
  private state = -1;

  constructor(ws: WebSocket, handlers: SequencerStep[]) {
    this.ws = ws;
    this.handlers = handlers;
    this.deferred = new Promise((resolve, reject) => {
      this._resolve = resolve;
      this._reject = reject;
    });
    ws.addEventListener("message", this.boundHandler);
    this.ws.send("seqReady");
    this.resolve(0);
  }

  promise(): Promise<void> {
    return this.deferred;
  }

  private async handler(e: MessageEvent) {
    this.messageBuffer.push(e.data);
    const index = this.messageBuffer.length;
    await this.handlers[this.state].handler(e.data, () => this.resolve(index));
  }

  private cleanup() {
    this.ws.removeEventListener("message", this.boundHandler);
    this._resolve!();
  }

  private resolve(index: number) {
    this.messageBuffer.splice(0, index);
    this.state++;
    const currentState = this.state;
    const currentHandler = this.handlers[currentState];
    clearTimeout(this.timeout);
    if (currentState >= this.handlers.length) {
      this.cleanup();
    } else if (currentHandler.timeout) {
      this.timeout = setTimeout(() => {
        this._reject!(
          `Sequence step ${
            currentHandler.key ?? currentState
          } timed out after ${currentHandler.timeout} ms`
        );
      }, currentHandler.timeout);
    }
    if (currentHandler.init) currentHandler.init();
    for (
      let i = 0;
      i < this.messageBuffer.length && this.state === currentState;
      i++
    ) {
      const index = i;
      currentHandler.handler(this.messageBuffer[i], () => this.resolve(index));
    }
  }

  async replay(handler: (e: MessageEvent) => void | PromiseLike<void>) {
    await Promise.all(this.messageBuffer.map((x) => handler(x)));
  }
}
